
import type { HeightFieldExtent } from "./heightfield";

/** RGBA as the records write it: four floats, red first, in 0..1.
 *  `CTextFileLoader::GetTokenColor` (`TextFileLoader.cpp:640-658`) is four
 *  `atof`s in file order and nothing else -- no 0..255 scaling anywhere. */
export type EnvColour = readonly [number, number, number, number];

export interface EnvLight {
  readonly enable: boolean;
  readonly diffuse: EnvColour;
  readonly ambient: EnvColour;
}

export interface MapEnvironment {
  /** The index's own key: `a1`, `c3`, `n-snowm01`, `skipia_dungeon@season2`. */
  readonly key: string;
  readonly directionalLight: {
    readonly direction: readonly [number, number, number];
    readonly background: EnvLight;
    readonly character: EnvLight;
  };
  /** `STATEMANAGER.SetMaterial` for everything drawn under the background light
   *  (`MapManager.cpp:237`). With `D3DRS_AMBIENT` pinned to 0
   *  (`EterLib/StateManager.cpp:190`), a fully lit terrain vertex is
   *  `emissive + ambient * light.ambient + diffuse * light.diffuse`. */
  readonly material: {
    readonly diffuse: EnvColour;
    readonly ambient: EnvColour;
    readonly emissive: EnvColour;
  };
  readonly fog: {
    readonly enable: boolean;
    /** Legacy world units, the unit map coordinates are in: 100 units = 1 m
     *  (`MapType.h:219-220`'s `c_Section_xCellSize = 100`; the server's own
     *  comments gloss `1000` as 10 m, `cmd_gm.cpp:844`). One unit is 10 mm, so
     *  `MM_PER_LEGACY_UNIT` converts. */
    readonly nearDistance: number;
    readonly farDistance: number;
    readonly colour: EnvColour;
  };
  readonly skyBox: {
    readonly gradient: readonly EnvColour[];
    readonly gradientLevelUpper: number;
    readonly gradientLevelLower: number;
    /** The cube's half-extents, in legacy units -- 3500 (35 m) in every shipped
     *  record. `CMapOutdoor::SetEnvironmentSkyBox` hands it to
     *  `CSkyBox::SetSkyBoxScale` (`MapOutdoor.cpp:296`), which is a pure
     *  `D3DXMatrixScaling` on a unit cube (`SkyBox.cpp:233-241`). */
    readonly scale: readonly [number, number, number];
    /** The cloud sheet: how wide it is and how far above the eye it floats,
     *  both in legacy units (`MapOutdoor.cpp:309-310`, and
     *  `CSkyObject::Update`'s `m_v3Position.z + m_fCloudHeight`,
     *  `SkyBox.cpp:143-145`). */
    readonly cloudScale: readonly [number, number];
    readonly cloudHeight: number;
    /** How many times the texture repeats across the sheet, and how fast it
     *  scrolls -- texture widths per second (`SkyBox.cpp:293-306`, `:891-897`). */
    readonly cloudTextureScale: readonly [number, number];
    readonly cloudSpeed: readonly [number, number];
    /** As the record spells it, artists' drive letter and all. `skybox.ts`'s
     *  `cloudTextureUrl` takes the base name, which is all the original's own
     *  case-insensitive resource lookup uses. Null when the group names none. */
    readonly cloudTextureFileName: string | null;
    /** `CloudColor`'s two rows. Only the first is read -- `SetCloudColor` puts
     *  `m_FirstColor` on all four corners (`SkyBox.cpp:560-611`) -- and it is
     *  the `D3DTA_DIFFUSE` the cloud's colour op adds through the texture's
     *  inverse alpha. */
    readonly cloudColour: readonly EnvColour[];
  };
  /** The `LensFlare` group (`MapUtil.cpp:196-203`). `enable` is `Enable 0` in
   *  every one of the 42 shipped records, and `CharacterizeFlare(false, ...)`
   *  turns the whole of `CLensFlare` off (`MapOutdoor.cpp:330-333`), textures
   *  included -- so a record has to say so before anything is fetched or
   *  drawn. See `render/lensflare.ts`. */
  readonly lensFlare: {
    readonly enable: boolean;
    readonly brightnessColour: EnvColour;
    readonly maxBrightness: number;
    readonly mainFlareEnable: boolean;
    /** As the record spells it, drive letter and all; `mainFlareTextureUrl`
     *  takes the base name. Null when the group names none. */
    readonly mainFlareTextureFileName: string | null;
    readonly mainFlareSize: number;
  };
}

/** `tools/maps.mjs`'s `MM_PER_LEGACY_UNIT`, restated for the browser: one
 *  legacy world unit is 10 mm. */
export const MM_PER_LEGACY_UNIT = 10;

export interface MapAssetUrls {
  /** The collision blob: walkability and water, one bit-pair per metre cell. */
  readonly terrain: string;
  /** The ground textures, stacked vertically. Shared between maps built from
   *  the same texture set -- b1 and c1 both name a1's, which is why this is a
   *  URL out of the manifest rather than one derived from the map's key. */
  readonly atlas: string;
  readonly tiles: string;
  readonly height: string;
  readonly heightMeta: string;
  readonly normal: string;
  readonly shadow: string;
  /** Optional: the colour a character is lit by, per metre of ground, from the
   *  same `shadowmap.raw` as `shadow`. A map built before it has none. */
  readonly shade?: string;
  /** Optional when the source map has no prop/property files. */
  readonly placements?: string;
  /** Optional: four maps ship only a JPEG-XR `minimap.wdp` the exporter cannot
   *  decode. See `OPTIONAL_ASSET_KEYS`. */
  readonly minimap?: string;
  /** Optional as a group when the source map has no prop/property files. */
  readonly meshIndex?: string;
  readonly meshBlob?: string;
  readonly textureIndex?: string;
  readonly textureBase?: string;
}

export interface ActiveMap extends HeightFieldExtent {
  /** The manifest's short key: `a1`, `a3`, `b1`, `c1`. */
  readonly key: string;
  /** The original's own name, e.g. `metin2_map_a3`. The same string `/mapz`
   *  returns and the `Welcome` frame carries. */
  readonly name: string;
  readonly assets: MapAssetUrls;
  readonly environment?: MapEnvironment;
}

/** A manifest that exists and does not describe a usable map. Distinct from a
 *  network failure, which is a reason to fall back; this is a broken build. */
export class MapManifestError extends Error {}

/** `app.py`'s `MAP_PATH`, relative -- see this module's doc for why the answer
 *  cannot wait for the `Welcome` frame. */
export const MAP_ENDPOINT = "/mapz";

/** `tools/build_map.mjs`'s output, at the path it writes it to. */
export const MANIFEST_URL = "maps/manifest.json";
export const MANIFEST_FETCH_RETRIES = 1;
export const MANIFEST_RETRY_DELAY_MS = 250;

export const A1_ENVIRONMENT: MapEnvironment = {
  key: "a1",
  directionalLight: {
    direction: [0.350156, 0.562609, -0.748907],
    background: { enable: true, diffuse: [1, 0.972549, 0.972549, 1], ambient: [0, 0, 0, 1] },
    character: { enable: true, diffuse: [1, 0.972549, 0.972549, 1], ambient: [0.15, 0.15, 0.15, 1] },
  },
  material: {
    diffuse: [1, 1, 1, 1],
    ambient: [0.882353, 0.854902, 0.784314, 1],
    emissive: [0.321569, 0.321569, 0.411765, 1],
  },
  fog: { enable: true, nearDistance: 5000, farDistance: 20000, colour: [0.690196, 0.741176, 0.839216, 1] },
  skyBox: {
    gradientLevelUpper: 4,
    gradientLevelLower: 1,
    gradient: [
      [0.094118, 0.286275, 0.658824, 0], [0.231373, 0.403922, 0.729412, 0],
      [0.231373, 0.403922, 0.729412, 0], [0.376471, 0.498039, 0.729412, 0],
      [0.376471, 0.498039, 0.729412, 0], [0.568627, 0.678431, 0.882353, 0],
      [0.568627, 0.678431, 0.882353, 0], [0.85098, 0.901961, 1, 0],
      [0.85098, 0.901961, 1, 0], [0.533333, 0.564706, 0.623529, 0],
    ],
    scale: [3500, 3500, 3500],
    cloudScale: [200_000, 200_000],
    cloudHeight: 30_000,
    cloudTextureScale: [4, 4],
    cloudSpeed: [0.004, 0.004],
    cloudTextureFileName: "d:/ymir work/environment/clouds_zone01.tga",
    cloudColour: [[0, 0, 0, 0], [0, 0, 0, 0]],
  },
  lensFlare: {
    enable: false,
    brightnessColour: [1, 0.886275, 0.886275, 1],
    maxBrightness: 0.74,
    mainFlareEnable: true,
    mainFlareTextureFileName: "D:\\Ymir Work\\environment\\sunflare.dds",
    mainFlareSize: 0.35,
  },
};

export const DEFAULT_ENVIRONMENT: MapEnvironment = {
  key: "default",
  directionalLight: {
    // `Environment_Init` leaves both lights black and disabled; only the
    // material and the fog have non-trivial defaults.
    direction: [0, 0, -1],
    background: { enable: false, diffuse: [1, 1, 1, 1], ambient: [0, 0, 0, 1] },
    character: { enable: false, diffuse: [1, 1, 1, 1], ambient: [0, 0, 0, 1] },
  },
  material: {
    diffuse: [0.8, 0.8, 0.8, 1],
    ambient: [0.8, 0.8, 0.8, 1],
    emissive: [0.8, 0.8, 0.8, 1],
  },
  fog: { enable: false, nearDistance: 12_800, farDistance: 17_920, colour: [0.5, 0.5, 0.5, 1] },
  // `Environment_Init` sets the skybox scale to 3500 and leaves everything else
  // zero (`GameLib/MapUtil.cpp:4-68`), so a record-less map draws no sky at all
  // -- `CSkyBox::Refresh` returns before it builds a face when the two gradient
  // levels sum to zero (`EterLib/SkyBox.cpp:437-438`).
  skyBox: {
    gradientLevelUpper: 0,
    gradientLevelLower: 0,
    gradient: [],
    scale: [3500, 3500, 3500],
    cloudScale: [0, 0],
    cloudHeight: 0,
    cloudTextureScale: [1, 1],
    cloudSpeed: [0, 0],
    cloudTextureFileName: null,
    cloudColour: [],
  },
  // `Environment_Init`: the flare is off, white, at full brightness, with no
  // main flare texture (`MapUtil.cpp:59-63`).
  lensFlare: {
    enable: false,
    brightnessColour: [1, 1, 1, 1],
    maxBrightness: 1,
    mainFlareEnable: false,
    mainFlareTextureFileName: null,
    mainFlareSize: 0,
  },
};

/** `tools/build_environment.mjs`'s index, at the path it writes it to. */
export const ENVIRONMENT_INDEX_URL = "environment/index.json";

export const NIGHT_ENVIRONMENT_KEY = "moonlight04";

export const A1_FALLBACK: ActiveMap = {
  key: "a1",
  name: "metin2_map_a1",
  worldWidthMm: 1_024_000,
  worldHeightMm: 1_280_000,
  assets: {
    terrain: "terrain_a1.bin",
    atlas: "terrain_atlas.jpg",
    tiles: "terrain_tiles.bin",
    height: "terrain_height.bin",
    heightMeta: "terrain_height.json",
    normal: "terrain_normal.png",
    shadow: "terrain_shadow.png",
    shade: "terrain_shade.png",
    placements: "placements.json",
    minimap: "minimap_overview.jpg",
    meshIndex: "meshes/index.json",
    meshBlob: "meshes/meshes.bin",
    textureIndex: "meshtex/index.json",
    textureBase: "meshtex",
  },
};

const REQUIRED_ASSET_KEYS = [
  "terrain",
  "atlas",
  "tiles",
  "height",
  "heightMeta",
  "normal",
  "shadow",
] as const;

const OPTIONAL_ASSET_KEYS = [
  "shade",
  "minimap",
  "placements",
  "meshIndex",
  "meshBlob",
  "textureIndex",
  "textureBase",
] as const;

export function mapEndpointFor(serverUrl: string | null): string | null {
  if (serverUrl === null || serverUrl === "") return null;
  try {
    const url = new URL(serverUrl);
    url.protocol = url.protocol === "wss:" ? "https:" : url.protocol === "ws:" ? "http:" : url.protocol;
    url.pathname = MAP_ENDPOINT;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
}

export function selectMap(manifest: unknown, wanted: string | null): ActiveMap {
  if (manifest === null || typeof manifest !== "object") {
    throw new MapManifestError("map manifest is not an object");
  }
  const payload = manifest as { maps?: unknown; bootMap?: unknown };
  const entries = Array.isArray(payload.maps) ? payload.maps : [];
  if (entries.length === 0) throw new MapManifestError("map manifest lists no maps");

  const target = wanted ?? (typeof payload.bootMap === "string" ? payload.bootMap : null);
  const chosen = target === null
    ? entries[0]
    : entries.find((entry) => {
        const record = entry as { key?: unknown; name?: unknown };
        return record.key === target || record.name === target;
      });

  if (chosen === undefined) {
    const available = entries
      .map((entry) => String((entry as { key?: unknown }).key ?? "?"))
      .join(", ");
    throw new MapManifestError(`no map named ${String(target)} in the manifest; built maps are: ${available}`);
  }
  return toActiveMap(chosen);
}

/** One manifest entry, checked. Every field is load-bearing, so a missing one
 *  is an error here rather than an `undefined` that reaches `fetch` and comes
 *  back as a request for the literal string "undefined". */
function toActiveMap(entry: unknown): ActiveMap {
  const record = entry as {
    key?: unknown;
    name?: unknown;
    widthMm?: unknown;
    heightMm?: unknown;
    assets?: unknown;
  };
  const key = record.key;
  const name = record.name;
  if (typeof key !== "string" || typeof name !== "string") {
    throw new MapManifestError("map entry has no key/name");
  }
  const width = record.widthMm;
  const height = record.heightMm;
  if (typeof width !== "number" || typeof height !== "number" || !(width > 0) || !(height > 0)) {
    throw new MapManifestError(`map ${key} has no usable widthMm/heightMm`);
  }
  const rawAssets = record.assets;
  if (rawAssets === null || typeof rawAssets !== "object") {
    throw new MapManifestError(`map ${key} has no assets block`);
  }
  const source = rawAssets as Record<string, unknown>;
  const assets: Record<string, string> = {};
  for (const asset of REQUIRED_ASSET_KEYS) {
    const url = source[asset];
    if (typeof url !== "string" || url === "") {
      throw new MapManifestError(`map ${key} names no ${asset}`);
    }
    assets[asset] = url;
  }
  for (const asset of OPTIONAL_ASSET_KEYS) {
    const url = source[asset];
    if (url !== undefined && (typeof url !== "string" || url === "")) {
      throw new MapManifestError(`map ${key} has invalid ${asset}`);
    }
    if (typeof url === "string") assets[asset] = url;
  }
  return {
    key,
    name,
    worldWidthMm: width,
    worldHeightMm: height,
    assets: assets as unknown as MapAssetUrls,
  };
}

export function selectEnvironment(index: unknown, wanted: string): MapEnvironment {
  if (index === null || typeof index !== "object") {
    throw new MapManifestError("environment index is not an object");
  }
  const payload = index as { environments?: unknown; maps?: unknown };
  const records = payload.environments;
  const maps = payload.maps;
  if (records === null || typeof records !== "object" || maps === null || typeof maps !== "object") {
    throw new MapManifestError("environment index has no environments/maps");
  }
  const key = (maps as Record<string, unknown>)[wanted.toLowerCase()];
  if (typeof key !== "string") {
    throw new MapManifestError(`no environment bound to ${wanted}`);
  }
  return readEnvironment(records as Record<string, unknown>, key);
}

/** One record by its index key -- the map bindings' target, and the way the
 *  night record is fetched. */
export function readEnvironment(records: Record<string, unknown>, key: string): MapEnvironment {
  const record = records[key];
  if (record === null || typeof record !== "object") {
    throw new MapManifestError(`environment index has no record ${key}`);
  }
  const source = record as Record<string, unknown>;
  const light = asObject(source.directionalLight, `${key}.directionalLight`);
  const material = asObject(source.material, `${key}.material`);
  const fog = asObject(source.fog, `${key}.fog`);
  const sky = asObject(source.skyBox, `${key}.skyBox`);
  const flare = asObject(source.lensFlare, `${key}.lensFlare`);
  return {
    key,
    directionalLight: {
      direction: asFloats(light.direction, 3, `${key}.direction`) as [number, number, number],
      background: asLight(light.background, `${key}.background`),
      character: asLight(light.character, `${key}.character`),
    },
    material: {
      diffuse: asColour(material.diffuse, `${key}.material.diffuse`),
      ambient: asColour(material.ambient, `${key}.material.ambient`),
      emissive: asColour(material.emissive, `${key}.material.emissive`),
    },
    fog: {
      enable: fog.enable === true,
      nearDistance: asNumber(fog.nearDistance, `${key}.fog.nearDistance`),
      farDistance: asNumber(fog.farDistance, `${key}.fog.farDistance`),
      colour: asColour(fog.colour, `${key}.fog.colour`),
    },
    skyBox: {
      gradientLevelUpper: asNumber(sky.gradientLevelUpper, `${key}.skyBox.gradientLevelUpper`),
      gradientLevelLower: asNumber(sky.gradientLevelLower, `${key}.skyBox.gradientLevelLower`),
      gradient: (Array.isArray(sky.gradient) ? sky.gradient : []).map((row, at) =>
        asColour(row, `${key}.skyBox.gradient[${at}]`),
      ),
      scale: asFloats(sky.scale, 3, `${key}.skyBox.scale`) as [number, number, number],
      cloudScale: asFloats(sky.cloudScale, 2, `${key}.skyBox.cloudScale`) as [number, number],
      cloudHeight: asNumber(sky.cloudHeight, `${key}.skyBox.cloudHeight`),
      cloudTextureScale: asFloats(
        sky.cloudTextureScale, 2, `${key}.skyBox.cloudTextureScale`,
      ) as [number, number],
      cloudSpeed: asFloats(sky.cloudSpeed, 2, `${key}.skyBox.cloudSpeed`) as [number, number],
      cloudTextureFileName:
        typeof sky.cloudTextureFileName === "string" ? sky.cloudTextureFileName : null,
      cloudColour: (Array.isArray(sky.cloudColour) ? sky.cloudColour : []).map((row, at) =>
        asColour(row, `${key}.skyBox.cloudColour[${at}]`),
      ),
    },
    lensFlare: {
      enable: flare.enable === true,
      brightnessColour: asColour(flare.brightnessColour, `${key}.lensFlare.brightnessColour`),
      maxBrightness: asNumber(flare.maxBrightness, `${key}.lensFlare.maxBrightness`),
      mainFlareEnable: flare.mainFlareEnable === true,
      mainFlareTextureFileName:
        typeof flare.mainFlareTextureFileName === "string"
          ? flare.mainFlareTextureFileName
          : null,
      mainFlareSize: asNumber(flare.mainFlareSize, `${key}.lensFlare.mainFlareSize`),
    },
  };
}

function asObject(value: unknown, what: string): Record<string, unknown> {
  if (value === null || typeof value !== "object") throw new MapManifestError(`${what} is missing`);
  return value as Record<string, unknown>;
}

function asNumber(value: unknown, what: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new MapManifestError(`${what} is not a number`);
  }
  return value;
}

function asFloats(value: unknown, count: number, what: string): number[] {
  if (!Array.isArray(value) || value.length < count) {
    throw new MapManifestError(`${what} is not ${count} numbers`);
  }
  return value.slice(0, count).map((part, at) => asNumber(part, `${what}[${at}]`));
}

function asColour(value: unknown, what: string): EnvColour {
  return asFloats(value, 4, what) as unknown as EnvColour;
}

function asLight(value: unknown, what: string): EnvLight {
  const source = asObject(value, what);
  return {
    enable: source.enable === true,
    diffuse: asColour(source.diffuse, `${what}.diffuse`),
    ambient: asColour(source.ambient, `${what}.ambient`),
  };
}

/** What `resolveMap` needs to know about the page it is booting on. Narrow on
 *  purpose: a `URLSearchParams` and a string are trivial to write in a test,
 *  where a `Location` and a `Window` are not. */
export interface MapRequest {
  readonly params: Pick<URLSearchParams, "get">;
  /** `serverurl.ts`'s answer: the socket this client will open, or null offline. */
  readonly serverUrl: string | null;
  /** Injected so a test can answer without a network. Defaults to the page's. */
  readonly fetchFn?: typeof fetch;
  readonly arrivingOn?: string | null;
}

export async function resolveMap(request: MapRequest): Promise<ActiveMap> {
  const fetchFn = request.fetchFn ?? globalThis.fetch.bind(globalThis);
  const arriving = request.arrivingOn ?? null;
  const override = arriving !== null && arriving !== ""
    ? arriving
    : request.params.get("map");
  const asked = override !== null && override !== "" ? override : null;

  // All three at once: the manifest is the bigger fetch and there is no reason
  // for it to queue behind a one-line JSON body it does not depend on, and the
  // environment index depends on neither.
  const [hosted, manifest, environments] = await Promise.all([
    asked !== null ? Promise.resolve(null) : hostedMap(fetchFn, request.serverUrl),
    fetchManifest(fetchFn),
    fetchEnvironments(fetchFn),
  ]);

  if (manifest === null) {
    // No manifest at all: the pre-pipeline tree, where a1 is the only map and
    // sits at the root under the names it has always had.
    if (asked !== null && asked !== "a1" && asked !== A1_FALLBACK.name) {
      console.warn(`?map=${asked} but no ${MANIFEST_URL}; loading ${A1_FALLBACK.name}`);
    }
    // a1's own record, spelled out here, for the same tree and the same reason
    // `A1_FALLBACK` is: this checkout has no `environment/index.json` either.
    return { ...A1_FALLBACK, environment: A1_ENVIRONMENT };
  }

  const wanted = asked ?? hosted;
  let chosen: ActiveMap;
  try {
    chosen = selectMap(manifest, wanted);
  } catch (error) {
    console.warn(`${String(error)}; loading the manifest's boot map instead`);
    chosen = selectMap(manifest, null);
  }
  return { ...chosen, environment: resolveEnvironment(environments, chosen.name) };
}

export function resolveEnvironment(index: unknown | null, mapName: string): MapEnvironment {
  if (index === null) return DEFAULT_ENVIRONMENT;
  try {
    return selectEnvironment(index, mapName);
  } catch (error) {
    console.warn(`${String(error)}; lighting ${mapName} by Environment_Init's defaults`);
    return DEFAULT_ENVIRONMENT;
  }
}

export function resolveNightEnvironment(index: unknown | null): MapEnvironment | null {
  if (index === null || typeof index !== "object") return null;
  const records = (index as { environments?: unknown }).environments;
  if (records === null || typeof records !== "object") return null;
  try {
    return readEnvironment(records as Record<string, unknown>, NIGHT_ENVIRONMENT_KEY);
  } catch (error) {
    console.warn(`${String(error)}; the night flag will not change the light`);
    return null;
  }
}

async function fetchEnvironments(fetchFn: typeof fetch): Promise<unknown | null> {
  try {
    const response = await fetchFn(ENVIRONMENT_INDEX_URL);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (error) {
    console.warn(`${ENVIRONMENT_INDEX_URL} unavailable (${String(error)})`);
    return null;
  }
}

/** The server's answer, or null if it did not give one. A shard too old to
 *  answer 404s here, which is a fallback to the boot map and not an error --
 *  that shard is hosting the boot map. */
async function hostedMap(fetchFn: typeof fetch, serverUrl: string | null): Promise<string | null> {
  const endpoint = mapEndpointFor(serverUrl);
  if (endpoint === null) return null;
  try {
    const response = await fetchFn(endpoint, { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { map?: unknown };
    return typeof body.map === "string" && body.map !== "" ? body.map : null;
  } catch (error) {
    console.warn(`${endpoint} did not say which map this shard hosts (${String(error)})`);
    return null;
  }
}

async function fetchManifest(fetchFn: typeof fetch): Promise<unknown | null> {
  try {
    return await fetchManifestWithRetry(fetchFn);
  } catch (error) {
    console.warn(`${MANIFEST_URL} unavailable (${String(error)}); assuming the single-map tree`);
    return null;
  }
}

/** A backgrounded Safari tab can interrupt the small manifest request while
 * leaving the renderer available. Retry once so a real hosted map is not
 * mistaken for the root a1 fallback; persistent failure keeps boot safe. */
export async function fetchManifestWithRetry(
  fetchFn: typeof fetch,
  wait: (milliseconds: number) => Promise<void> = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
): Promise<unknown> {
  let failure: unknown;
  for (let attempt = 0; attempt <= MANIFEST_FETCH_RETRIES; attempt++) {
    try {
      const response = await fetchFn(MANIFEST_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      failure = error;
      if (attempt === MANIFEST_FETCH_RETRIES) break;
      await wait(MANIFEST_RETRY_DELAY_MS);
    }
  }
  throw failure;
}
